import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, QrCode } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetGuestRestaurants, apiGuestLogin, apiLookupStoreByNameHint } from '@/services/api';
import { getSelectedMerchantId, setSelectedMerchantId, storeRoute } from '@/lib/routes';
import PwaInstallPrompt from '@/components/guest/PwaInstallPrompt';
import InvoiceScannerModal from '@/components/guest/InvoiceScannerModal';

const PENDING_GUEST_PHONE_KEY = 'flyback_pending_guest_phone';
const PENDING_GUEST_OTP_KEY = 'flyback_pending_guest_otp';
const ENTRY_MERCHANT_KEY = 'flyback_entry_merchant_id';
const ENTRY_MERCHANT_KEY_V2 = 'entry_merchant_id';
const ENTRY_CAMPAIGN_KEY = 'flyback_entry_campaign_id';
const QR_UPLOAD_KEY = 'flyback_signup_qr_upload';
const COUNTRY_OPTIONS = [
  { code: '+1', label: '+1' },
  { code: '+91', label: '+91' },
];

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.slice(-10);
}

function toE164(countryCode, localPhone) {
  const code = String(countryCode || '+1').trim();
  const digits = normalizePhone(localPhone);
  if (!digits) return '';
  return `${code}${digits}`;
}

function getEntryMerchantId() {
  return (
    String(window.localStorage.getItem(ENTRY_MERCHANT_KEY) || '').trim() ||
    String(window.localStorage.getItem(ENTRY_MERCHANT_KEY_V2) || '').trim()
  );
}

function setEntryMerchantId(value) {
  const merchantId = String(value || '').trim();
  if (!merchantId) return;
  window.localStorage.setItem(ENTRY_MERCHANT_KEY, merchantId);
  window.localStorage.setItem(ENTRY_MERCHANT_KEY_V2, merchantId);
}

function clearEntryMerchantId() {
  window.localStorage.removeItem(ENTRY_MERCHANT_KEY);
  window.localStorage.removeItem(ENTRY_MERCHANT_KEY_V2);
}

function extractQrContext(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return { merchantId: '', campaignId: '', isPlatform: false };

  try {
    const parsed = new URL(raw, window.location.origin);
    const path = String(parsed.pathname || '').trim();
    const isStoreRoute = /^\/store(?:\/|$)/i.test(path);
    const fromQuery = String(parsed.searchParams.get('merchant_id') || '').trim();
    const campaignId = String(parsed.searchParams.get('campaign_id') || '').trim();
    if (fromQuery && isStoreRoute) {
      return { merchantId: fromQuery, campaignId, isPlatform: true };
    }
    const pathMatch = path.match(/\/store\/([^/?#]+)/i);
    if (pathMatch?.[1]) {
      return { merchantId: decodeURIComponent(pathMatch[1]).trim(), campaignId, isPlatform: true };
    }
  } catch {}

  const queryMatch = raw.match(/[?&]merchant_id=([^&#\s]+)/i);
  const campaignMatch = raw.match(/[?&]campaign_id=([^&#\s]+)/i);
  if (queryMatch?.[1] && /\/store(?:\/|\?|$)/i.test(raw)) {
    return {
      merchantId: decodeURIComponent(queryMatch[1]).trim(),
      campaignId: campaignMatch?.[1] ? decodeURIComponent(campaignMatch[1]).trim() : '',
      isPlatform: true,
    };
  }

  const pathMatch = raw.match(/\/store\/([^/?#\s]+)/i);
  if (pathMatch?.[1]) {
    return {
      merchantId: decodeURIComponent(pathMatch[1]).trim(),
      campaignId: campaignMatch?.[1] ? decodeURIComponent(campaignMatch[1]).trim() : '',
      isPlatform: true,
    };
  }

  try {
    const parsedJson = JSON.parse(raw);
    const merchantId = String(parsedJson?.merchant_id || '').trim();
    const campaignId = String(parsedJson?.campaign_id || '').trim();
    if (merchantId) {
      return { merchantId, campaignId, isPlatform: true };
    }
  } catch {}

  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(raw)) {
    return { merchantId: raw, campaignId: '', isPlatform: true };
  }

  return { merchantId: '', campaignId: '', isPlatform: false };
}

async function createCenterCropFile(imageFile, cropRatio = 0.82) {
  const objectUrl = URL.createObjectURL(imageFile);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objectUrl;
    });

    const minSide = Math.min(img.width, img.height);
    const cropSide = Math.floor(minSide * Math.max(0.45, Math.min(cropRatio, 0.95)));
    const sx = Math.floor((img.width - cropSide) / 2);
    const sy = Math.floor((img.height - cropSide) / 2);

    const canvas = document.createElement('canvas');
    canvas.width = cropSide;
    canvas.height = cropSide;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, cropSide, cropSide);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 1));
    if (!blob) return null;
    return new File([blob], `qr-crop-${cropRatio}.png`, { type: 'image/png' });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function loadImageElementFromFile(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function createCenterCropCanvas(image, cropRatio = 0.82) {
  const minSide = Math.min(image.width, image.height);
  const cropSide = Math.floor(minSide * Math.max(0.32, Math.min(cropRatio, 0.98)));
  const sx = Math.floor((image.width - cropSide) / 2);
  const sy = Math.floor((image.height - cropSide) / 2);
  const canvas = document.createElement('canvas');
  canvas.width = cropSide;
  canvas.height = cropSide;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, sx, sy, cropSide, cropSide, 0, 0, cropSide, cropSide);
  return canvas;
}

async function decodeQrWithBarcodeDetector(file) {
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) {
    return '';
  }

  const image = await loadImageElementFromFile(file);
  const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
  const canvases = [createCenterCropCanvas(image, 0.98), createCenterCropCanvas(image, 0.86), createCenterCropCanvas(image, 0.72), createCenterCropCanvas(image, 0.6), createCenterCropCanvas(image, 0.46)];

  for (const canvas of canvases) {
    try {
      const rows = await detector.detect(canvas);
      const raw = String(rows?.[0]?.rawValue || '').trim();
      if (raw) return raw;
    } catch {}
  }
  return '';
}

function normalizeQrImageFileHint(filename) {
  let safe = String(filename || '').trim().toLowerCase();
  if (!safe) return '';
  safe = safe.replace(/\.[a-z0-9]{2,5}$/i, '');
  safe = safe.replace(/\(\d+\)$/i, '').trim();
  safe = safe.replace(/&/g, ' and ');
  safe = safe.replace(/[^a-z0-9]+/g, '-');
  safe = safe.replace(/-{2,}/g, '-');
  safe = safe.replace(/^-+|-+$/g, '');
  const suffixes = ['-store-qr', '-store', '-qr-pack', '-qr-code', '-qrcode', '-qr'];
  for (const suffix of suffixes) {
    if (safe.endsWith(suffix)) {
      safe = safe.slice(0, -suffix.length);
      break;
    }
  }
  return safe.trim();
}

async function decodeQrFromUploadedFile(file) {
  const { Html5Qrcode } = await import('html5-qrcode');
  const qrReaderId = 'signup-upload-qr-reader';
  const scanner = new Html5Qrcode(qrReaderId);
  const attempts = [file];
  const crop82 = await createCenterCropFile(file, 0.82);
  const crop72 = await createCenterCropFile(file, 0.72);
  const crop62 = await createCenterCropFile(file, 0.62);
  if (crop82) attempts.push(crop82);
  if (crop72) attempts.push(crop72);
  if (crop62) attempts.push(crop62);

  try {
    for (const candidate of attempts) {
      try {
        const decoded = await scanner.scanFile(candidate, false);
        if (decoded) return decoded;
      } catch {}
    }

    const barcodeDetected = await decodeQrWithBarcodeDetector(file);
    if (barcodeDetected) return barcodeDetected;

    throw new Error('QR_DECODE_FAILED');
  } finally {
    await scanner.clear().catch(() => {});
  }
}

export default function GuestPhoneLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    guestTheme,
    guestToken,
    guestLogin,
    setCurrentMerchantId,
    setStoreInfo,
    setMerchantInfo,
    setWallet,
    setTransactions,
  } = useStore();

  const [mode, setMode] = useState('signin');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [anniversaryDate, setAnniversaryDate] = useState('');
  const [qrMode, setQrMode] = useState('scan');
  const [qrUploadName, setQrUploadName] = useState('');
  const [qrUploadDecoding, setQrUploadDecoding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showStoreQrScanner, setShowStoreQrScanner] = useState(false);

  const cleanPhone = useMemo(() => normalizePhone(phone), [phone]);
  const fullPhone = useMemo(() => toE164(countryCode, phone), [countryCode, phone]);
  const maxDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    const incomingMerchantId = String(searchParams.get('merchant_id') || '').trim();
    if (incomingMerchantId) {
      setEntryMerchantId(incomingMerchantId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!guestToken) return;
    const qrMerchantId = getEntryMerchantId();
    if (qrMerchantId) {
      navigate(storeRoute(qrMerchantId, 'wallet'), { replace: true });
      return;
    }
    const selectedMerchantId = getSelectedMerchantId();
    if (selectedMerchantId) {
      navigate(storeRoute(selectedMerchantId, 'wallet'), { replace: true });
      return;
    }
    navigate('/store/select', { replace: true });
  }, [guestToken, navigate]);

  const applyGuestSession = useCallback((result, selectedMerchantId) => {
    const merchantId = String(selectedMerchantId || result?.merchant?.id || '').trim();
    if (!merchantId) return;
    setWallet(null);
    setTransactions([]);
    setStoreInfo(null);
    setMerchantInfo(null);
    setCurrentMerchantId(merchantId);
    guestLogin(result.token, result.customer, result.merchant);
    setSelectedMerchantId(merchantId);
  }, [guestLogin, setCurrentMerchantId, setMerchantInfo, setStoreInfo, setTransactions, setWallet]);

  const applyScannedMerchant = useCallback(async (scanContext) => {
    const merchantId = String(scanContext?.merchantId || '').trim();
    const campaignId = String(scanContext?.campaignId || '').trim();
    const isPlatform = Boolean(scanContext?.isPlatform);
    if (!isPlatform) {
      setError('Invalid QR code. Please scan a FlyBack restaurant QR.');
      setShowStoreQrScanner(false);
      return;
    }
    if (!merchantId || merchantId === 'demo') {
      setError('Invalid QR code. Please scan a valid restaurant QR.');
      setShowStoreQrScanner(false);
      return;
    }

    setError('');
    setEntryMerchantId(merchantId);
    if (campaignId) {
      window.localStorage.setItem(ENTRY_CAMPAIGN_KEY, campaignId);
    } else {
      window.localStorage.removeItem(ENTRY_CAMPAIGN_KEY);
    }
    window.localStorage.removeItem('selected_merchant_id');
    setSelectedMerchantId(merchantId);
    setCurrentMerchantId(merchantId);

    // Option 1 UX: if signup details already filled, complete signup immediately.
    if (mode === 'signup') {
      const cleanName = String(name || '').trim();
      if (cleanPhone.length >= 10 && cleanName.length >= 2) {
        setLoading(true);
        try {
          const result = await apiGuestLogin(fullPhone, '', cleanName, merchantId, {
            date_of_birth: dateOfBirth || null,
            anniversary_date: anniversaryDate || null,
          });
          applyGuestSession(result, merchantId);
          clearEntryMerchantId();
          setShowStoreQrScanner(false);
          navigate(storeRoute(merchantId, 'wallet'), { replace: true });
          return;
        } catch (e) {
          const detail = e?.response?.data?.detail;
          if (typeof detail === 'string' && /otp|sign in/i.test(detail)) {
            setMode('signin');
            setError('Account already exists for this restaurant. Please sign in with OTP.');
          } else {
            setError(typeof detail === 'string' ? detail : 'Unable to create account right now.');
          }
          setShowStoreQrScanner(false);
          return;
        } finally {
          setLoading(false);
        }
      }
    }

    setShowStoreQrScanner(false);
  }, [anniversaryDate, applyGuestSession, cleanPhone, dateOfBirth, fullPhone, mode, name, navigate, setCurrentMerchantId]);

  const handleStoreQrScan = useCallback(async (decodedText) => {
    const context = extractQrContext(decodedText);
    await applyScannedMerchant(context);
  }, [applyScannedMerchant]);

  const submitSignInOtp = useCallback(async (otpValue) => {
    const cleanOtp = String(otpValue || '').trim();
    if (loading) return;

    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (cleanOtp.length < 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const qrMerchantId = getEntryMerchantId();
      if (qrMerchantId) {
        const result = await apiGuestLogin(fullPhone, cleanOtp, '', qrMerchantId);
        applyGuestSession(result, qrMerchantId);
        clearEntryMerchantId();
        navigate(storeRoute(qrMerchantId), { replace: true });
        return;
      }

      // Normal manual flow: choose from associated restaurants next.
      const restaurants = await apiGetGuestRestaurants(fullPhone);
      if (!restaurants.length) {
        setError('No restaurants are linked with this phone number yet. Use Sign Up after scanning a restaurant QR.');
        return;
      }

      setStoreInfo(null);
      setMerchantInfo(null);
      setWallet(null);
      setTransactions([]);
      setCurrentMerchantId('demo');
      setSelectedMerchantId('');
      window.sessionStorage.setItem(PENDING_GUEST_PHONE_KEY, fullPhone);
      window.sessionStorage.setItem(PENDING_GUEST_OTP_KEY, cleanOtp);
      navigate('/store/select', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string' && /create an account/i.test(detail)) {
        setMode('signup');
        setOtp('');
        setError('No account found for this restaurant. Please complete sign up.');
        return;
      }
      setError(typeof detail === 'string' ? detail : 'Unable to verify phone right now.');
    } finally {
      setLoading(false);
    }
  }, [applyGuestSession, cleanPhone, fullPhone, loading, navigate, setCurrentMerchantId, setMerchantInfo, setStoreInfo, setTransactions, setWallet]);

  const submitSignUp = useCallback(async () => {
    if (loading) return;
    const cleanName = String(name || '').trim();

    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (cleanName.length < 2) {
      setError('Please enter your full name.');
      return;
    }

    const qrMerchantId = getEntryMerchantId();
    if (!qrMerchantId) {
      setError('Please scan a restaurant QR to continue sign up.');
      setShowStoreQrScanner(true);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await apiGuestLogin(fullPhone, '', cleanName, qrMerchantId, {
        date_of_birth: dateOfBirth || null,
        anniversary_date: anniversaryDate || null,
      });
      applyGuestSession(result, qrMerchantId);
      clearEntryMerchantId();
      navigate(storeRoute(qrMerchantId), { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string' && /otp|sign in/i.test(detail)) {
        setMode('signin');
        setError('Account already exists. Please sign in with OTP.');
        return;
      }
      setError(typeof detail === 'string' ? detail : 'Unable to create account right now.');
    } finally {
      setLoading(false);
    }
  }, [anniversaryDate, applyGuestSession, cleanPhone, dateOfBirth, fullPhone, loading, name, navigate]);

  const handleQrImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid QR image file.');
      return;
    }
    setQrUploadName(file.name || 'qr-image');
    setQrUploadDecoding(true);
    setError('');
    try {
      const decodedText = await decodeQrFromUploadedFile(file);
      const decodedContext = extractQrContext(decodedText);
      if (!decodedContext?.isPlatform || !decodedContext?.merchantId || decodedContext.merchantId === 'demo') {
        throw new Error('NON_PLATFORM_QR_DECODED');
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      window.sessionStorage.setItem(QR_UPLOAD_KEY, dataUrl);
      await applyScannedMerchant(decodedContext);
    } catch {
      try {
        const hint = normalizeQrImageFileHint(file.name);
        if (!hint) {
          throw new Error('EMPTY_HINT');
        }
        const merchant = await apiLookupStoreByNameHint(hint);
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        window.sessionStorage.setItem(QR_UPLOAD_KEY, dataUrl);
        await applyScannedMerchant({
          merchantId: String(merchant?.id || '').trim(),
          campaignId: '',
          isPlatform: true,
        });
      } catch {
        setError('Invalid QR code. Please upload a valid FlyBack restaurant QR code image.');
      }
    } finally {
      setQrUploadDecoding(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
    if (value.length === 6 && mode === 'signin') {
      submitSignInOtp(value);
    }
  };

  return (
    <div className={`${guestTheme === 'dark' ? 'dark ' : ''}min-h-screen flex flex-col items-center justify-center px-6 bg-[#faf5f5] dark:bg-background`} data-testid="guest-phone-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-16 h-16 rounded-2xl gradient-brand flex items-center justify-center">
            <img src="/assets/flyback.png" alt="FlyBack" className="w-30 h-30 object-contain" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              FlyBack Guest Access
            </h1>
            <p className="text-[12px] text-muted-foreground">
              {mode === 'signin' ? 'Sign in with phone + OTP' : 'Sign up for your first restaurant visit'}
            </p>
          </div>
        </div>

        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border space-y-5">
          <div className="rounded-xl border border-border bg-secondary/40 p-1 grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => {
                setMode('signin');
                setError('');
              }}
              className={`h-9 rounded-lg text-sm font-semibold transition-colors ${mode === 'signin' ? 'gradient-brand-btn text-white' : 'text-muted-foreground'}`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('signup');
                setError('');
              }}
              className={`h-9 rounded-lg text-sm font-semibold transition-colors ${mode === 'signup' ? 'gradient-brand-btn text-white' : 'text-muted-foreground'}`}
            >
              Sign Up
            </button>
          </div>

          {mode === 'signup' && (
            <div className="space-y-2">
              <label className="text-sm font-semibold">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full h-12 rounded-xl border border-border bg-background px-3 text-sm outline-none"
                data-testid="guest-signup-name-input"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-semibold">Phone Number</label>
            <div className="flex items-center h-12 rounded-xl border border-border bg-background overflow-hidden">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="h-full px-2.5 text-sm text-foreground border-r border-border bg-transparent outline-none"
                data-testid="guest-country-code-select"
              >
                {COUNTRY_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                data-testid="guest-phone-input"
                type="tel"
                placeholder="00000 00000"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (error) setError('');
                }}
                className="flex-1 h-full px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                inputMode="numeric"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">Saved as: {fullPhone || `${countryCode}XXXXXXXXXX`}</p>
          </div>

          {mode === 'signup' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold">Date of Birth (Optional)</label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={maxDate}
                  placeholder="Select your birth date"
                  className="w-full h-12 rounded-xl border border-border bg-background px-3 text-sm outline-none"
                  data-testid="guest-signup-dob-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold">Anniversary (Optional)</label>
                <input
                  type="date"
                  value={anniversaryDate}
                  onChange={(e) => setAnniversaryDate(e.target.value)}
                  max={maxDate}
                  placeholder="Select anniversary date"
                  className="w-full h-12 rounded-xl border border-border bg-background px-3 text-sm outline-none"
                  data-testid="guest-signup-anniversary-input"
                />
              </div>
            </>
          )}

          {mode === 'signin' && (
            <div className="space-y-2">
              <label className="text-sm font-semibold">OTP</label>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={handleOtpChange} data-testid="guest-otp-input">
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="w-11 h-12 text-lg font-bold text-foreground rounded-xl border-2 bg-secondary/50 first:rounded-l-xl last:rounded-r-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                Login continues automatically when OTP is complete.
              </p>
            </div>
          )}

          {mode === 'signup' && (
            <div className="space-y-2">
              <label className="text-sm font-semibold">Restaurant QR Setup</label>
              <div className="grid grid-cols-2 gap-2 rounded-xl border border-border p-1 bg-secondary/35">
                <button
                  type="button"
                  onClick={() => {
                    setQrMode('scan');
                    setError('');
                  }}
                  className={`h-9 rounded-lg text-xs font-semibold ${qrMode === 'scan' ? 'gradient-brand-btn text-white' : 'text-muted-foreground bg-transparent'}`}
                >
                  Scan QR
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setQrMode('upload');
                    setShowStoreQrScanner(false);
                    setError('');
                  }}
                  className={`h-9 rounded-lg text-xs font-semibold ${qrMode === 'upload' ? 'gradient-brand-btn text-white' : 'text-muted-foreground bg-transparent'}`}
                >
                  Upload QR
                </button>
              </div>
              {qrMode === 'upload' ? (
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Upload Restaurant QR Code</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQrImageUpload}
                    className="w-full h-12 rounded-xl border border-border bg-background px-3 text-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-semibold"
                    data-testid="guest-signup-qr-upload"
                  />
                  {qrUploadName ? <p className="text-[11px] text-muted-foreground truncate">Uploaded: {qrUploadName}</p> : null}
                  {qrUploadDecoding ? (
                    <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Decoding QR image…</p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Upload the QR image shared by the restaurant.</p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setShowStoreQrScanner(true);
                  }}
                  className="w-full h-10 rounded-xl border border-border bg-background text-sm font-semibold hover:bg-secondary/50 flex items-center justify-center gap-2"
                  data-testid="scan-restaurant-qr-btn"
                >
                  <QrCode className="w-4 h-4" />
                  Scan Restaurant QR
                </button>
              )}
            </div>
          )}

          {mode === 'signup' && (
            <button
              type="button"
              onClick={submitSignUp}
              disabled={loading}
              className="w-full h-11 rounded-xl gradient-brand-btn text-white text-sm font-semibold disabled:opacity-60"
              data-testid="guest-signup-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          )}

          {error && <p className="text-destructive text-sm text-center" data-testid="guest-phone-login-error">{error}</p>}
          {loading && mode === 'signin' && (
            <div className="flex items-center justify-center text-sm text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
            </div>
          )}
        </div>
      </motion.div>
      <PwaInstallPrompt />
      <div id="signup-upload-qr-reader" className="hidden" />
      <InvoiceScannerModal
        open={showStoreQrScanner}
        onClose={setShowStoreQrScanner}
        onScan={handleStoreQrScan}
        title="Scan Restaurant QR"
        description="Scan your restaurant QR to set the correct restaurant context before sign up."
        hint="Use the QR displayed by the restaurant. Invalid or non-restaurant QRs will be rejected."
        modalTestId="store-qr-scanner-modal"
      />
    </div>
  );
}
